/**
 * Text that arrives a character at a time, and leaves the same way.
 *
 * The console reads as something *printing* rather than something rendering,
 * and that is the whole of the effect: a section appearing is a machine typing
 * it, and a section going is the same machine taking it back.
 *
 * ── The flicker problem, which is the reason this is not four lines ─────────
 *
 * Sections come and go on conditions that are not stable. Mass lock is the
 * obvious one: fly along a body's boundary and it engages, drops, and engages
 * again several times in a few seconds. With an untype-on-exit animation that
 * is a panel thrashing — half a line disappearing and coming back, repeatedly,
 * which reads as a fault rather than as an effect.
 *
 * So leaving is *delayed*. A section asked to go waits `SETTLE` before it starts
 * untyping, and a section asked to come back during that window simply carries
 * on as though nothing happened. Conditions that genuinely end still animate
 * out; conditions that merely wobble never begin.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Characters a second, typing in.
 *
 * Slow enough to read as a machine printing rather than as text appearing. The
 * first version ran at 220, which at a glance is indistinguishable from no
 * effect at all — and it was hiding a worse bug besides, see below.
 */
const TYPE_RATE = 95;

/** Taking it back is quicker. Nobody is reading what is being removed. */
const ERASE_RATE = 260;

/**
 * How long a section must stay unwanted before it starts to leave.
 *
 * Long enough to swallow a mass lock flickering at a boundary, short enough
 * that dismissing a panel still feels like it responded to the click.
 */
const SETTLE = 400;

export type TypingPhase = 'typing' | 'holding' | 'erasing' | 'gone';

export interface Typed {
  /** What to render this frame. */
  shown: string;
  phase: TypingPhase;
  /** True once an erased section has finished and can be unmounted. */
  gone: boolean;
}

/**
 * Type `text` in while `present`, and take it back out when it stops.
 *
 * Returns the visible prefix rather than driving the DOM, so the caller decides
 * what to do with it — the console wraps it in a header and a border, and a
 * caller that wanted it in a `<pre>` could.
 *
 * Changing `text` while present retypes from wherever the two stop matching, so
 * a readout whose distance changed does not replay its own name.
 */
export function useTyping(text: string, present = true): Typed {
  /*
   * Always starts empty, and that is the whole fix.
   *
   * This was `useState(present ? text : '')`, which meant anything that mounted
   * wanted showed its full text on the first frame and never typed at all —
   * only *later edits* animated. Every log line mounts present, so the feed in
   * particular had no effect on it whatever, which reads as "too fast" and was
   * really "not running".
   */
  const [shown, setShown] = useState('');
  const [phase, setPhase] = useState<TypingPhase>(present ? 'typing' : 'gone');

  /** The live values, for a timer that must not close over a stale render. */
  const want = useRef({ text, present });
  want.current = { text, present };

  /*
   * The animation's own copy, advanced in the frame callback.
   *
   * Kept beside the state rather than read out of it because the previous
   * version computed the next frame *inside* a `setShown` updater and called
   * `setPhase` from in there. React is free to run an updater more than once —
   * StrictMode does exactly that, and the app is wrapped in one — so the
   * animation advanced twice per frame and fired a second setState from inside
   * a reducer. Stepping here and setting state once at the end is both correct
   * and easier to follow.
   */
  const current = useRef('');
  const at = useRef<TypingPhase>(present ? 'typing' : 'gone');

  const frame = useRef(0);
  const settling = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let last = performance.now();

    const step = (now: number) => {
      if (cancelled) return;
      frame.current = requestAnimationFrame(step);

      // Capped, so a tab returning from the background types its backlog in
      // rather than completing it in one frame.
      const delta = Math.min(0.1, (now - last) / 1000);
      last = now;

      const target = want.current.text;
      let text_ = current.current;
      let phase_ = at.current;

      if (want.current.present) {
        // Coming back cancels any settle in progress: it should resume, not
        // finish a countdown nobody is waiting for.
        settling.current = 0;

        /*
         * The common prefix is where typing resumes.
         *
         * A readout whose last line changed should not retype its first — so
         * what is already correct is kept and only the tail is replaced. A small
         * change stays cheap; a large one still looks deliberate.
         */
        let shared = 0;
        while (
          shared < text_.length &&
          shared < target.length &&
          text_[shared] === target[shared]
        ) {
          shared++;
        }

        if (text_.length > shared) {
          // Something that was there is now wrong: take it back first.
          text_ = text_.slice(0, Math.max(shared, text_.length - Math.ceil(ERASE_RATE * delta)));
          phase_ = 'typing';
        } else if (text_.length < target.length) {
          text_ = target.slice(0, Math.min(target.length, text_.length + Math.ceil(TYPE_RATE * delta)));
          phase_ = text_.length >= target.length ? 'holding' : 'typing';
        } else {
          phase_ = 'holding';
        }
      } else {
        // Not wanted. Wait out the settle before touching anything, so a
        // condition that merely wobbled is never animated away.
        if (settling.current === 0) settling.current = now;

        if (now - settling.current >= SETTLE) {
          text_ = text_.slice(0, Math.max(0, text_.length - Math.ceil(ERASE_RATE * delta)));
          phase_ = text_.length === 0 ? 'gone' : 'erasing';
        }
      }

      if (text_ !== current.current) {
        current.current = text_;
        setShown(text_);
      }
      if (phase_ !== at.current) {
        at.current = phase_;
        setPhase(phase_);
      }
    };

    frame.current = requestAnimationFrame(step);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame.current);
    };
  }, []);

  return { shown, phase, gone: phase === 'gone' && !present };
}
